import { useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO } from '@/components/shared/SEO';
import { Eye, EyeOff, Check } from 'lucide-react';

const proofPoints = [
  'Monitor competitor ads across Meta, TikTok, and Google',
  'Track pricing and stock changes in real time',
  'Detect storefront tech signals and strategy shifts',
  'Get intelligent alerts delivered to your inbox',
  'Join 2,000+ Shopify merchants using RADAR',
];

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { signUp } = useAuth();
  const navigate = useNavigate();

  const passwordRequirements = [
    { regex: /.{8,}/, text: 'At least 8 characters' },
    { regex: /[A-Z]/, text: 'One uppercase letter' },
    { regex: /[a-z]/, text: 'One lowercase letter' },
    { regex: /[0-9]/, text: 'One number' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signUp({ name, email, password, confirmPassword });
      try {
        const storedConnectId =
          typeof window !== 'undefined'
            ? window.localStorage.getItem('radar_shopify_connect_id')
            : null;
        if (storedConnectId) {
          navigate(`/shopify-connect?code=${storedConnectId}`);
        } else {
          navigate('/dashboard');
        }
      } catch {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingLayout footer="none">
      <SEO title="Start your free trial" description="Create your RADAR account. 7 days free. No credit card required." canonical="/register" noindex />
      <Section hero className="min-h-screen flex items-center">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-5xl mx-auto">
            {/* Left - Proof points */}
            <div>
              <AnimatedReveal>
                <Link to="/" className="flex items-center gap-2.5 mb-8">
                  <div className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-lime-500/10 border border-lime-500/20">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-lime-500">
                      <circle cx="12" cy="12" r="3" fill="currentColor" />
                      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 3" />
                    </svg>
                  </div>
                  <span className="text-lg font-display font-bold text-foreground">RADAR</span>
                </Link>
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">Competitive intelligence for operators who move fast</h2>
                <ul className="space-y-4">
                  {proofPoints.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-lime-500 flex-shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </AnimatedReveal>
            </div>

            {/* Right - Register form */}
            <AnimatedReveal delay={0.15}>
              <GlassPanel className="p-8 sm:p-10" data-testid="auth-form">
                <h1 className="text-2xl font-display font-bold text-foreground mb-2">Start your free trial</h1>
                <p className="text-sm text-muted-foreground mb-8">Create your RADAR account. 7 days free. No credit card required.</p>

                {error && (
                  <div className="mb-5 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-2">Full name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Smith"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30 transition-colors"
                      data-testid="auth-name-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-2">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30 transition-colors"
                      data-testid="auth-email-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-2">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a password"
                        className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 pr-12 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30 transition-colors"
                        data-testid="auth-password-input"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>

                    {password && (
                      <div className="mt-2 space-y-1">
                        {passwordRequirements.map((req, i) => (
                          <div key={i} className={`flex items-center gap-1.5 text-xs ${req.regex.test(password) ? 'text-lime-400' : 'text-muted-foreground'}`}>
                            <Check className={`h-3 w-3 ${req.regex.test(password) ? 'opacity-100' : 'opacity-30'}`} />
                            {req.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-2">Confirm password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30 transition-colors"
                      required
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    By creating an account, you agree to our{' '}
                    <Link to="/terms" className="text-lime-400 hover:text-lime-300 transition-colors">Terms of Service</Link>
                    {', '}
                    <Link to="/privacy" className="text-lime-400 hover:text-lime-300 transition-colors">Privacy Policy</Link>
                    {', and '}
                    <Link to="/gdpr" className="text-lime-400 hover:text-lime-300 transition-colors">GDPR &amp; EU rights</Link>.
                  </p>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_20px_rgba(204,255,108,0.15)] disabled:opacity-50"
                    data-testid="auth-submit-button"
                  >
                    {loading ? 'Creating account...' : 'Create account'}
                  </button>
                </form>

                <div className="mt-6 text-center text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/login" className="text-lime-400 hover:text-lime-300 transition-colors">Log in</Link>
                </div>
              </GlassPanel>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>
    </MarketingLayout>
  );
}
